import { forwardRef } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { cn } from "../lib/cn";

const buttonVariants = cva(
  // Base: MUI Button feel — 8px radius, no uppercase transform, label type ramp
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium no-underline transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-1 focus-visible:ring-offset-surface-canvas disabled:pointer-events-none disabled:opacity-38 select-none",
  {
    variants: {
      variant: {
        /** Amber — the Billda primary action. Dark text on amber in both modes. */
        primary:
          "bg-surface-primary text-on-primary hover:bg-surface-primary-hover active:bg-surface-primary-active",
        /** Blue-grey chrome — MUI's old "contained" primary. */
        neutral:
          "bg-surface-inverse text-on-inverse hover:bg-surface-inverse-hover active:bg-surface-inverse-active",
        success:
          "bg-surface-success text-on-success hover:bg-surface-success-hover active:bg-surface-success-active",
        warning:
          "bg-surface-warning text-on-warning hover:bg-surface-warning-hover active:bg-surface-warning-active",
        outline:
          "border border-border-strong bg-transparent text-content-primary hover:bg-surface-raised active:bg-surface-pressed",
        outlineWarning:
          "border border-border-warning-strong bg-transparent text-content-warning hover:bg-surface-warning-subtle active:bg-surface-warning-subtle",
        /** Destructive secondary action — e.g. Sign Out, Remove access. */
        outlineDanger:
          "border border-border-error-strong bg-transparent text-content-error hover:bg-surface-error-subtle active:bg-surface-error-subtle-active",
        /** MUI "text" button — quiet inline action. */
        ghost:
          "bg-transparent text-content-primary hover:bg-surface-raised active:bg-surface-pressed",
        ghostDanger:
          "bg-transparent text-content-error hover:bg-surface-error-subtle active:bg-surface-error-subtle-active",
        danger:
          "bg-surface-error text-on-error hover:bg-surface-error-hover active:bg-surface-error-active",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-[0.9375rem]",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 p-0",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">,
    VariantProps<typeof buttonVariants> {
  /** Render as child element (e.g. router Link) instead of a button. */
  asChild?: boolean;
  /** Shows a spinner and disables interaction. */
  loading?: boolean;
  children?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : "button";
    return (
      <Component
        ref={ref}
        data-slot="button"
        data-variant={variant ?? "primary"}
        data-size={size ?? "md"}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
            {children}
          </>
        ) : (
          children
        )}
      </Component>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
